/**
 * useLgpdLegalHolds — hook Legal Holds LGPD (Brief 5 B1 Centro Direitos).
 *
 * Sprint Frontend Desktop W3.2. Consome endpoints W2.2 lgpd/ módulo (PR #65).
 *
 * Capabilities server-side:
 *   - lgpd-requests-read (Team+) — listagem
 *   - lgpd-legal-holds-manage (DPO / admin_tenant) — criar + levantar
 *
 * ADRs: ADR-024 + ADR-098.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api-client';

import type {
  LegalHoldStatus,
  LegalHoldType,
  LegalHoldWireItem,
} from '../features/dpo/centro-direitos/types';

const QUERY_KEY = 'lgpd-legal-holds';
const STALE_TIME = 60_000;

export function useLegalHolds(params: { status?: LegalHoldStatus; entityId?: string } = {}): {
  data: LegalHoldWireItem[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: [QUERY_KEY, params.status ?? 'all', params.entityId ?? 'all'],
    queryFn: async () => {
      const res = await api.listLegalHolds({
        ...(params.status !== undefined ? { status: params.status } : {}),
        ...(params.entityId !== undefined ? { entityId: params.entityId } : {}),
      });
      return res.data;
    },
    staleTime: STALE_TIME,
    retry: 1,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

export function useCreateLegalHold(): ReturnType<
  typeof useMutation<
    { holdId: string },
    Error,
    {
      entityType: string;
      entityId: string;
      holdType: LegalHoldType;
      reason: string;
      reference?: string;
      expiresAt?: string;
    }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.createLegalHold(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useLiftLegalHold(): ReturnType<
  typeof useMutation<{ status: string }, Error, { holdId: string; liftReason: string }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ holdId, liftReason }: { holdId: string; liftReason: string }) =>
      api.liftLegalHold(holdId, liftReason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}
